import { BaseWindow } from "../WindowAbstractions/BaseWindow";
import { RouterClient } from "../../../../clients/routerClient";
import { Logger } from "../../../../clients/logger";
import { WorkspaceClient } from "../../../../clients/workspaceClient";
import { WindowClient } from "../../../../clients/windowClient";

import DockingCalculator from "../Docking/dockingMain";
import DockableWindow from "../Docking/dockableWindow";
import { MonitorDimensions } from "../Docking/types";
import { FinsembleWindowData } from "../types";
import { findKey } from "lodash";
import async from "async";
import { WINDOWSTATE } from "../../../../common/constants";

import { WORKSPACE } from "../../../../common/constants";
const { GET_MANY_WINDOW_STATES } = WORKSPACE.API_CHANNELS;

type WindowsData = {
	aspectRatio: number;
	top: number;
	bottom: number;
	left: number;
	right: number;
	dockableWindow: DockableWindow;
	windows?: WindowsData[];
	isGroup?: boolean;
	name?: string;
};

type ResizeQueue = {
	dockableWin: DockableWindow;
	height: number;
	width: number;
	top: number;
	left: number;
};

Logger.start();

/**
 * TERRY: Auto-arrange organizes windows on the specific requested monitor (not all monitors). From a UX perspective, the monitor being
 * auto-arranged is always the one that contains the toolbar. To arrange a different monitor, the user must move their toolbar to that
 * monitor. It would be nice if a user could auto-arrange a monitor from the system tray for each monitor!
 *
 * This class remembers the state of windows before they were arranged. So if you press the auto-arrange button a second time then the
 * windows revert back to their previously unarranged state. This is an important feature. Customers complained until we added it.
 *
 * All minimized windows on that monitor are restored during an auto-arrange.
 *
 * Groups are treated as a single window.
 *
 * max and min widths/heights are ignored by the algorithm but I think they are reenforced by the setBounds call (or docking calculator). So what would probably happen is that a window with maxWidth would be allocated the same portion of space as if it had
 * no constraint, but then the constraint would prevent it from filling in all that space. The net result would be some empty space on the screen.
 * It might not be too complicated to take the actual width constraints into consideration in the calculation itself. Heights might be trickier.
 *
 */
class AutoArrange {
	isArranged: object;

	DockingCalculator: DockingCalculator;

	constructor(params) {
		this.isArranged = {};
		this.DockingCalculator = params.DockingCalculator;
	}

	/**
	 * TERRY: This is the core algorithm
	 * For more, see {@link https://medium.com/@jtreitz/the-algorithm-for-a-perfectly-balanced-photo-gallery-914c94a5d8af#.c09v4fn1e}. This is a javascript port of a python solution to the linear partition problem.
	 * @param {Array} aspectRatios An array of numbers; in this case, aspect ratios.
	 * @param {Number} rows Number of rows to distribute aspectRatios across.
	 * @returns {Array} ans Returns an array of arrays. Each internal array represents a row.
	 * @private
	 */
	linearPartition(aspectRatios: WindowsData[], rows: number): WindowsData[][] {
		let numWindows = aspectRatios.length;

		if (rows <= 0) {
			return [];
		}
		if (rows > numWindows) {
			return aspectRatios.map((x) => [x]);
		}

		// Make some two dimensional arrays filled with zeros
		const table: number[][] = Array.from({ length: numWindows }, () => Array.from({ length: rows }, () => 0));
		const solution: number[][] = Array.from({ length: numWindows - 1 }, () =>
			Array.from({ length: rows - 1 }, () => 0)
		);

		for (let i = 0; i < numWindows; i++) {
			const prevValue = i ? table[i - 1][0] : 0;
			table[i][0] = aspectRatios[i].aspectRatio + prevValue;
		}

		for (let j = 0; j < rows; j++) {
			table[0][j] = aspectRatios[0].aspectRatio;
		}

		const computeAspects = (i: number, j: number) => {
			let list_of_pairs: [number, number][] = [];
			for (var x = 0; x < i; x++) {
				list_of_pairs = [];
				for (let y = 0; y < i; y++) {
					const max = Math.max(table[y][j - 1], table[i][0] - table[y][0]);
					list_of_pairs.push([max, y]);
				}
			}
			const m = list_of_pairs.reduce(
				(previous, current) => (current[0] < previous[0] ? current : previous),
				[Infinity]
			);
			table[i][j] = m[0];
			solution[i - 1][j - 1] = m[1];
		};

		for (let i = 1; i < numWindows; i++) {
			for (let j = 1; j < rows; j++) {
				computeAspects(i, j);
			}
		}

		numWindows = numWindows - 1;
		rows = rows - 2;
		let ans: WindowsData[][] = [];
		while (rows >= 0) {
			const sub_list: WindowsData[] = [];
			for (let i = solution[numWindows - 1][rows] + 1; i < numWindows + 1; i++) {
				sub_list.push(aspectRatios[i]);
			}
			ans = [sub_list].concat(ans);
			numWindows = solution[numWindows - 1][rows];
			rows--;
		}

		const beginning_list: WindowsData[] = [];
		for (let i = 0; i < numWindows + 1; i++) {
			beginning_list.push(aspectRatios[i]);
		}
		return [beginning_list].concat(ans);
	}

	/**
	 * TERRY - Just returns true if the window is on the monitor that is being arranged.
	 * @private
	 * @param {LauncherClient~windowDescriptor} win
	 * @param {monitorDimensions} monitorDimensions
	 * @returns {boolean} Whether window is on the current monitor.
	 */
	windowOnMonitor(windowDescriptor, monitorDimensions) {
		// if right or left edge is within the window's bounds.
		return windowDescriptor.monitor.name === monitorDimensions.name;
	}

	/**
	 * Takes a list of windows and returns only the ones that are subject to being auto-arranged
	 * TERRY: i.e. check the config for the app. We'll need a new allowAutoArrange config that defaults to true.
	 */
	getArrangeableWindows(windowDescriptorList: FinsembleWindowData[]): FinsembleWindowData[] {
		return windowDescriptorList.filter((obj) => {
			// filter result is true if window is auto-arrangeable; result is false if not arrangeable
			if (!obj) {
				Logger.system.warn("autoArrange.getWindowsOnMonitor: unexpected null object");
				return false;
			} else if (!this.DockingCalculator.getWindow(obj.name, false)) {
				Logger.system.warn("autoArrange.getWindowsOnMonitor: window unknown");
				return false;
			} else {
				return obj?.allowAutoArrange ?? true;
			}
		});
	}

	// returns the windows on the monitor that will be auto arranged
	// TERRY: This is just a filter to find which windows are on the monitor being arranged
	getWindowsOnMonitor(windowDescriptorList: FinsembleWindowData[], monitorDimensions: MonitorDimensions) {
		// first filter out windows that are not auto arrangeable
		const descriptors = this.getArrangeableWindows(windowDescriptorList);

		const windowsOnMonitor: WindowsData[] = [];
		// only capture the arrangeable windows that are on the monitor
		for (let i = 0; i < descriptors.length; i++) {
			const descriptor = descriptors[i];
			const win = this.DockingCalculator.getWindow(descriptor.name);
			win.monitor = this.DockingCalculator.getMonitorForWindow(win);
			const noCachedPositionYet = !this.DockingCalculator.cachedPositions[win.monitor.name][win.name];
			const isOnMonitor = this.windowOnMonitor(win, monitorDimensions);
			if (noCachedPositionYet && isOnMonitor) {
				this.DockingCalculator.cachedPositions[win.monitor.name][win.name] = {
					left: win.left,
					right: win.right,
					bottom: win.bottom,
					top: win.top,
					height: win.height,
					width: win.width,
				};

				windowsOnMonitor.push({
					aspectRatio: win.width / win.height,
					top: win.top,
					bottom: win.bottom,
					left: win.left,
					right: win.right,
					dockableWindow: win,
				});
			}
		}

		// before arranging, sort window list based on location to influence final arrangement;
		// note the comparison function is not transitive, so results aren't always what expected;
		// however, they are usually intuitive when moving a single window.
		windowsOnMonitor.sort((a, b) => {
			if (a.top === b.top && a.left === b.left) {
				return 0; // a == b
			} else if (a.top < b.top && a.left < b.left) {
				return -1; // a < b
			} else if (a.top < b.bottom && a.left < b.left) {
				return -1; // a < b
			} else {
				return 1; // a > b
			}
		});

		// TERRY, this is where minimized windows are restored before being arranged.
		windowsOnMonitor.forEach((finWin) => {
			const wrap = this.DockingCalculator.getWindow(finWin.dockableWindow.name);
			// only restore minimized windows
			if (
				wrap.win.windowState === BaseWindow.WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED ||
				wrap.win.windowState === BaseWindow.WINDOWSTATE.MINIMIZED_WAS_NORMAL
			) {
				wrap.restore();
				wrap.bringToFront();
			}
		});
		return windowsOnMonitor;
	}

	/**
	 * Get windowData for all windows in the active workspace
	 * TERRY: This basically just means all active windows, but only those that would be saved with the layout. This is basically
	 * every window without ignoreFromLayouts + groups. We probably should not auto-arrange the toolbar/launcher. In Finsemble, the toolbar
	 * is always docked, but even a floating toolbar probably shouldn't be arranged (though it should probably be brought to front
	 * after the arrange.)
	 */
	async getAllWindowsInActiveWorkspace() {
		const windowNamesFromActiveWorkspace = (await WorkspaceClient.getActiveWorkspace()).data.windows;

		//retrieve states from WorkspaceService as WindowStorageManager should be a global singleton
		//This is not exposed through the WorkspaceClient as we don't want to expand API surface area available to clients
		const params = {
			type: "windowData",
			windowNames: windowNamesFromActiveWorkspace,
		};
		Logger.system.debug("AutoArrange: retrieving window states from WorkspaceService", params);
		const { response, err } = await RouterClient.query(GET_MANY_WINDOW_STATES, params);
		if (err) {
			Logger.system.error(
				"AutoArrange: An error occurred while retrieving the window states for the current workspace!",
				err
			);
		}

		return response.data as FinsembleWindowData[];
	}

	/**
	 * boxesToArrange is a concatenation of windows and groups built into singular objects
	 * so they can be treated as 'boxes to rearrange' rather than individual windows
	 *
	 * groupBoxes is an array of 'windows' that are actually a group of windows.
	 * Built into a single object that looks like a 'window' by the arrangement code
	 */
	private getBoxesToArrange({
		windowsFromActiveWorkspaceFiltered,
		monitorDimensions,
	}: {
		windowsFromActiveWorkspaceFiltered: FinsembleWindowData[];
		monitorDimensions: MonitorDimensions;
	}): WindowsData[] {
		let boxesToArrange: WindowsData[] = [];
		let groupBoxes: WindowsData[] = [];
		// get groups of windows
		// TERRY: In Finsemble, groups are actually collections of individual windows, so this algorithm is more complex because
		// it has to maintain those windows. In io.CD this should be a lot simpler since a group is a just a window.
		const groupsFromWorkspace = this.DockingCalculator.getGroups();

		// get individual windows
		const windowsOnMonitor = this.getWindowsOnMonitor(windowsFromActiveWorkspaceFiltered, monitorDimensions);

		// Map through each individual window to determine if it is part of a group
		windowsOnMonitor.forEach((win) => {
			// Uses lodash to search through the workspaces to see if this win
			// is contained in the group's 'windows' array
			const currentWindowsGroupBoxName = findKey(groupsFromWorkspace, (thisGroup) => {
				if (!thisGroup.isMovable) return false;
				// The array returned by DockingCalculator.getMovableGroups and getGroups has a length of 0 with the windows attached to the object.
				// That doesn't seem ideal, but for now handle that case
				const result = Object.values(thisGroup.windows).findIndex(
					(windowInsideGroup) => windowInsideGroup.name === win.dockableWindow.name
				);
				//findIndex returns a -1 for false which does not work as a falsy value. Since findKey expects a bool we must translate the result.
				return result !== -1;
			});

			// See if a 'win' has already been built for this group. Retrieve it if so, otherwise
			// one will be created.
			const groupBox =
				groupBoxes.length > 0
					? groupBoxes.find((thisGroupBox) => thisGroupBox.name === currentWindowsGroupBoxName)
					: null;

			if (!currentWindowsGroupBoxName) {
				// There is no group that this win belongs to, add it to the final array
				boxesToArrange.push(win);
			} else if (groupBox) {
				// There is already a 'win' for this group in windowsGroups, we need to
				// average the win aspect ratios. This may have to change
				const groupIndex =
					groupBoxes.length > 0
						? groupBoxes.findIndex(
								(thisGroupBox) => thisGroupBox.name === groupsFromWorkspace[currentWindowsGroupBoxName].name
							)
						: null;
				groupBox.aspectRatio =
					groupsFromWorkspace[currentWindowsGroupBoxName].width /
					groupsFromWorkspace[currentWindowsGroupBoxName].height;
				// Add the new win to the groups windows array
				groupBox.windows?.push(win);
				//Both -1 and null are possible failure options when defining group index depending on which ternary statement was hit
				if (groupIndex !== null && groupIndex !== -1) {
					groupBoxes.splice(groupIndex, 1);
					groupBoxes.push(groupBox);
				}
			} else {
				// There is no 'win' for this group. Creating one
				const newGroupBox = {
					aspectRatio: win.aspectRatio,
					bottom: groupsFromWorkspace[currentWindowsGroupBoxName].bottom,
					left: groupsFromWorkspace[currentWindowsGroupBoxName].left,
					name: groupsFromWorkspace[currentWindowsGroupBoxName].name,
					right: groupsFromWorkspace[currentWindowsGroupBoxName].right,
					top: groupsFromWorkspace[currentWindowsGroupBoxName].top,
					windows: [win],
					isGroup: true,
					dockableWindow: win.dockableWindow,
				};
				groupBoxes.push(newGroupBox);
			}
		});

		// Add the array of groupWindows to the array already containing loner windows
		return boxesToArrange.concat(groupBoxes);
	}

	animateWindow(request): Promise<void> {
		return new Promise((resolve) => {
			const { height, width, top, left, dockableWin } = request;
			const bounds = {
				height,
				width,
				top,
				left,
			};
			dockableWin.animatePositionAndHeight(bounds, () => {
				dockableWin.win._bringToFront({});
				this.DockingCalculator.buildSnapRelationships(dockableWin);
				dockableWin.win.stopMove();
				RouterClient.transmit(`DockingService.${dockableWin.name}`, {
					command: "saveWindowLocation",
					bounds,
				});
				resolve();
			});
		});
	}

	/**
	 * Arranges all windows in the current workspace that are visible on the current monitor. For more about the algorithm used to distribute windows across rows, see {@link https://medium.com/@jtreitz/the-algorithm-for-a-perfectly-balanced-photo-gallery-914c94a5d8af#.c09v4fn1e this post}. The algorithm chooses the number of rows by taking the square root of the number of windows on the monitor and rounding down. 3 windows results in 1 row. 4 windows results in 2 rows. 8 windows will be arranged across 2 rows. 9 windows will be arranged across 3 rows. Other breakpoints are 16, 25, and 36.
	 * @param {Object} monitorDimensions Monitor dimensions of a given monitor. This value is passed
	 * in from {@link util#getMonitorInfo}.
	 */
	async arrange(monitorDimensions: MonitorDimensions) {
		if (this.isArranged[monitorDimensions.name]) {
			/*
				11/7/19 JC - Previously this was toggling the status (!<currentStatus>). If we're reverting,
				the status will always be false, if we're not reverting (below) then the status will be true.
				Toggling leads to potential for internal state mismatch
			*/
			this.isArranged[monitorDimensions.name] = false;
			// Reverts windows and sends statusUpdate.
			this.revertArrangedWindows(monitorDimensions);
			// TERRY, this upadtes an external variable that causes the auto-arrange icon to be highlighted or not
			// io.CD will need a new way to deal with this, and probably API calls that the UI element can use
			this.DockingCalculator.sendAutoArrangeStatusUpdate();
			return;
		}

		this.isArranged[monitorDimensions.name] = true;
		this.DockingCalculator.sendAutoArrangeStatusUpdate();

		// Window list used to come from Launcher's activeWindow list.
		// The workspace should be the one reporting the windows in a workspace
		// that need to be auto arranged.

		// Workspace returns a list of windows, including StackedWindows.
		// This will have duplicates as each window in stack will also appear as
		// an individual window in the list
		// TERRY: A "stack" is a tabbed group. In Finsemble each window in a tabbed group is still an individual window that needs
		// to be ignored from the auto-arrange. This should not be an issue in io.CD.
		const windowsFromActiveWorkspace = await this.getAllWindowsInActiveWorkspace();

		// Filter out any hidden windows (including hidden tabs)
		const state = this.DockingCalculator.store.getState();
		const windowsFromActiveWorkspaceFiltered = windowsFromActiveWorkspace.filter((w) => {
			// The "StackedWindow" exists as a workspace window but will not be manipulated directly.
			// It's excluded from this error check as it's not saved in the store as a window.
			if (!w?.name?.startsWith("StackedWindow-") && !state.main.windows[w.name]) {
				Logger.system.error(
					`AutoArrange: Window not found in state: ${w.name}. keys:`,
					Object.keys(state.main.windows)
				);
			}
			if (w.windowState == WINDOWSTATE.MAXIMIZED) {
				return false;
			}
			return state.main.windows[w.name]?.physicalWindowState?.visible ?? true;
		});

		const boxesToArrange = this.getBoxesToArrange({ windowsFromActiveWorkspaceFiltered, monitorDimensions });

		const resizeQueue: ResizeQueue[] = [];

		// Number of rows is determined by the square root of the number of windows on the monitor.
		// This gives us a preference for N x N grids. If you have 9 windows, you get 3 rows.
		// If the aspect ratios are set correctly, you get 3x3.
		const rowGroups = this.linearPartition(boxesToArrange, Math.floor(Math.sqrt(boxesToArrange.length)));
		let rowHeight = Math.floor(monitorDimensions.height / rowGroups.length);
		// Iterates through the properly partitioned windows and queues up the movements.
		rowGroups.forEach(async (windowsInGroup, groupIndex) => {
			const windowTop = Math.round(monitorDimensions.top + groupIndex * rowHeight);
			const rowWidth = Math.round(monitorDimensions.width / windowsInGroup.length);
			// if we're on the last row and the number of rows is not divisible by the monitor's height,
			// we need to add the rounding error to the height so that there isn't a gap
			// between the taskbar and the bottom edge of our last row.
			const rowHeightTotal = rowHeight * rowGroups.length;
			if (groupIndex === rowGroups.length - 1 && rowHeightTotal !== monitorDimensions.height) {
				// since we round down the initial height calculation, rowHeightTotal
				// will always be less than the monitor's height.
				rowHeight = rowHeight + (monitorDimensions.height - rowHeightTotal);
			}
			// Now that the position is set, queue up the movement.
			windowsInGroup.forEach((windowData, windex) => {
				// The 'windows' that are being looped through are are sometimes individual windows
				// and other times they are a group of windows. If its a group, we need to loop
				// through the group and add each window in the group. Fitting it into the space allotted for a single window
				if (windowData.isGroup) {
					// in this case dockableWin is actually a dockableGroup
					const left = rowWidth * windex + monitorDimensions.left;
					const right = left + rowWidth;
					const groupName: string = windowData.name ?? "";
					const group = this.DockingCalculator.getGroup(groupName);
					const bounds = {
						top: windowTop,
						left,
						right,
						height: rowHeight,
						width: rowWidth,
						triggeredByAutoArrange: false,
					};

					// For non-rectangular groups the left most window is the anchor and is placed at the top left,
					// but there may be windows in the group that are higher
					const offset = this.getGroupTopOffset(windowData.windows, windowData.dockableWindow);

					// TriggeredByAutoArrange will prevent the dockingService from deleting the windows
					// cachedPositions. We want to maintain these so that when we revert, grouped windows
					// return to where they expect
					group.moveTo(
						{
							anchor: windowData.dockableWindow,
							top: windowTop + offset,
							left,
							height: rowHeight,
							triggeredByAutoArrange: true,
						},
						() => {
							bounds.triggeredByAutoArrange = true;
							// We scale the group to the entire monitor. The 'anchor' here is basically "from what side did it get resized?".
							// Imagine the user grabbing the bottom right of the group and stretching it to fill the entire monitor. That's essentially what we're doing here.
							group.scale(bounds, "bottomRight", this.DockingCalculator as any);
						}
					);
				} else {
					resizeQueue.push({
						dockableWin: this.DockingCalculator.getWindow(windowData.dockableWindow.name),
						height: rowHeight,
						width: rowWidth,
						top: windowTop,
						left: rowWidth * windex + monitorDimensions.left,
					});
				}
			});

			for (let i = 0; i < resizeQueue.length; i++) {
				await this.animateWindow(resizeQueue[i]);
			}

			// animate each window. when they're all done, call the callback, form snapping relationships, and leave this function.
			if (resizeQueue.length) {
				this.DockingCalculator.formGroup(resizeQueue[0].dockableWin.name, {
					isMovable: false,
				});
				this.isArranged[monitorDimensions.name] = true;

				// Workaround to show a grouping button if no one window hasn't been grouped.
				// Emulates actions after manual snapping.
				if (boxesToArrange.length === windowsFromActiveWorkspaceFiltered.length) {
					const monitorForWindow = this.DockingCalculator.getMonitorForWindow(resizeQueue[0].dockableWin);
					this.DockingCalculator.onMoveComplete(
						{ name: resizeQueue[0].dockableWin.name, monitor: monitorForWindow },
						true
					);
				}
			}
		});
	}

	/**
	 * If any windows are higher than the anchor window, get the biggest top value difference between the highest
	 * window and the anchor window.
	 *
	 * @param windows
	 * @param anchor
	 */
	getGroupTopOffset(windows: WindowsData[] | undefined, anchor: DockableWindow): number {
		let offset = 0;
		windows?.forEach((window) => {
			const diff = anchor.top - window.top;
			if (diff > offset) {
				offset = diff;
			}
		});

		return offset;
	}

	/**
	 * Returns windows to where they were before an auto-arrange occurred.
	 */
	revertArrangedWindows(monitorDimensions) {
		const self = this;
		const windowObjectsToRevert = self.DockingCalculator.cachedPositions[monitorDimensions.name];
		const namesOfWindowsToRevert = Object.keys(windowObjectsToRevert);

		async function revertWindow(windowName) {
			const bounds = windowObjectsToRevert[windowName];

			let dockableWindow = self.DockingCalculator.getWindow(windowName);
			delete windowObjectsToRevert[windowName];

			// If the promoted tab has been changed when the stack window is auto-arranged, then we need to update the correct tab on the revert
			// TERRY, this probably is a non-issue for io.CD
			if (dockableWindow.allowTabbing) {
				const { data } = await WindowClient.getTabCollectionForWindow({ windowName: windowName });
				const { data: promotedTab } = await WindowClient.getPromotedTab({ tabCollectionId: data?.id || "" });
				if (promotedTab && promotedTab !== windowName) {
					// sets the promoted tab as active window for bounds restoring
					dockableWindow = self.DockingCalculator.getWindow(promotedTab);
				}
			}

			await new Promise<void>((resolve) => {
				// TERRY, we never implemented animation. This is really just a setBounds
				dockableWindow.animatePositionAndHeight(
					{
						height: bounds.height,
						width: bounds.width,
						top: bounds.top,
						left: bounds.left,
						right: bounds.right,
						duration: 250,
					},
					() => {
						RouterClient.transmit(`DockingService.${dockableWindow.name}`, {
							command: "saveWindowLocation",
							bounds,
						});
						resolve();
					}
				);
			});
		}

		// TERRY, this can probably all be removed. This was due to Finsemble's complexity of having to keep individual windows
		// formed into groups and tab collections, keeping track of snapped state, etc
		function onLocationReverted() {
			namesOfWindowsToRevert.forEach((windowName) => {
				const dockableWindow = self.DockingCalculator.getWindow(windowName);
				self.DockingCalculator.buildSnapRelationships(dockableWindow);
				self.DockingCalculator.checkGroupMembership(dockableWindow);
				dockableWindow.win.stopMove();
				delete self.DockingCalculator.cachedPositions[monitorDimensions.name][windowName];
			});

			Logger.system.debug("Publishing Group Update: Revert Auto-arrange");
			self.DockingCalculator.updateGroupData();
		}

		async.each(namesOfWindowsToRevert, revertWindow, onLocationReverted);

		self.DockingCalculator.sendAutoArrangeStatusUpdate();
	}
}

export default AutoArrange;
